import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, BarChart3, ChefHat, ChevronRight, Clock, DollarSign, Eye, FileText, LayoutDashboard, LineChart, Menu, PieChart, Play, ShieldCheck, TrendingUp, Users, Utensils, Wallet, X, Zap, TrendingDown, AlertCircle, Layers, MousePointer2, Globe, CheckCircle2 } from 'lucide-react';
interface ROILandingPageProps {
  onBack?: () => void;
}
export function ROI({
  onBack
}: ROILandingPageProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen bg-[#0A0A0B] text-white font-sans selection:bg-[#10B981] selection:text-white overflow-x-hidden" data-id="element-2415">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 border-b ${isScrolled ? 'bg-[#0A0A0B]/80 backdrop-blur-xl border-white/10 py-4' : 'bg-transparent border-transparent py-6'}`} data-id="element-2416">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between" data-id="element-2417">
          <div className="flex items-center gap-2 group cursor-pointer" data-id="element-2418">
            <div className="w-10 h-10 bg-gradient-to-br from-[#10B981] to-[#059669] rounded-xl flex items-center justify-center shadow-lg shadow-[#10B981]/20 group-hover:scale-110 transition-transform duration-500" data-id="element-2419">
              <TrendingUp size={24} className="text-white" data-id="element-2420" />
            </div>
            <span className="text-2xl font-black tracking-tighter" data-id="element-2421">ROI</span>
          </div>

          <div className="hidden lg:flex items-center gap-10 text-sm font-medium text-white/60" data-id="element-2422">
            <a href="#problem" className="hover:text-white transition-colors" data-id="element-2423">
              Problem
            </a>
            <a href="#how-it-works" className="hover:text-white transition-colors" data-id="element-2424">
              Solution
            </a>
            <a href="#benefits" className="hover:text-white transition-colors" data-id="element-2425">
              Benefits
            </a>
            <a href="#about" className="hover:text-white transition-colors" data-id="element-2426">
              About
            </a>
          </div>

          <div className="flex items-center gap-4" data-id="element-2427">
            {onBack && <button onClick={onBack} className="hidden md:block text-xs font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors mr-4" data-id="element-2428">
                ← Back to Admin
              </button>}
            <button className="hidden sm:block px-6 py-2.5 text-sm font-bold text-white/80 hover:text-white transition-colors" data-id="element-2429">
              Log In
            </button>
            <button className="px-6 py-2.5 bg-white text-black rounded-full text-sm font-bold hover:bg-[#10B981] hover:text-white transition-all duration-500 shadow-xl shadow-white/5" data-id="element-2430">
              Book a Demo
            </button>
            <button className="lg:hidden p-2 text-white/60 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(true)} data-id="element-2431">
              <Menu size={24} data-id="element-2432" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-2433">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        x: '100%'
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-0 z-[200] bg-[#0A0A0B] p-8 flex flex-col" data-id="element-2434">
            <div className="flex justify-between items-center mb-12" data-id="element-2435">
              <div className="flex items-center gap-2" data-id="element-2436">
                <div className="w-10 h-10 bg-[#10B981] rounded-xl flex items-center justify-center" data-id="element-2437">
                  <TrendingUp size={24} data-id="element-2438" />
                </div>
                <span className="text-2xl font-black tracking-tighter" data-id="element-2439">
                  ROI
                </span>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-white/60" data-id="element-2440">
                <X size={32} data-id="element-2441" />
              </button>
            </div>
            <div className="flex flex-col gap-8 text-3xl font-bold" data-id="element-2442">
              <a href="#problem" onClick={() => setMobileMenuOpen(false)} data-id="element-2443">
                Problem
              </a>
              <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} data-id="element-2444">
                Solution
              </a>
              <a href="#benefits" onClick={() => setMobileMenuOpen(false)} data-id="element-2445">
                Benefits
              </a>
              <a href="#about" onClick={() => setMobileMenuOpen(false)} data-id="element-2446">
                About
              </a>
            </div>
            <div className="mt-auto pt-8 border-t border-white/10 flex flex-col gap-4" data-id="element-2447">
              <button className="w-full py-4 bg-white text-black rounded-2xl font-bold text-lg" data-id="element-2448">
                Book a Demo
              </button>
              <button className="w-full py-4 border border-white/10 rounded-2xl font-bold text-lg" data-id="element-2449">
                Log In
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="relative pt-40 pb-32 px-6 overflow-hidden" data-id="element-2450">
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none" data-id="element-2451">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#10B981]/10 blur-[120px] rounded-full" data-id="element-2452"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-emerald-900/20 blur-[120px] rounded-full" data-id="element-2453"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-2454">
          <div className="text-center max-w-4xl mx-auto mb-20" data-id="element-2455">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full text-[#10B981] text-xs font-bold uppercase tracking-widest mb-8 backdrop-blur-md" data-id="element-2456">
              <span className="relative flex h-2 w-2" data-id="element-2457">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75" data-id="element-2458"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]" data-id="element-2459"></span>
              </span>
              Next-Gen Restaurant Intelligence
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-5xl md:text-8xl font-black tracking-tighter leading-[0.9] mb-8" data-id="element-2460">
              Stop Guessing Where Your <br data-id="element-2461" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-white/40" data-id="element-2462">
                Restaurant Money Is Going
              </span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-xl md:text-2xl text-white/60 font-medium leading-relaxed mb-12 max-w-3xl mx-auto" data-id="element-2463">
              See your real food costs, labor costs, and spending — faster. ROI
              helps restaurant owners understand their money in, money out, and
              operational blind spots.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-2464">
              <button className="w-full sm:w-auto px-10 py-5 bg-white text-black rounded-full font-black text-lg hover:bg-[#10B981] hover:text-white transition-all duration-500 shadow-2xl shadow-white/10 group flex items-center justify-center gap-2" data-id="element-2465">
                Book a Demo
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-2466" />
              </button>
              <button className="w-full sm:w-auto px-10 py-5 bg-white/5 border border-white/10 rounded-full font-black text-lg hover:bg-white/10 transition-all duration-500 backdrop-blur-md flex items-center justify-center gap-2" data-id="element-2467">
                <Play size={20} fill="currentColor" data-id="element-2468" />
                See How It Works
              </button>
            </motion.div>
          </div>

          {/* Dashboard Mockup */}
          <motion.div initial={{
          opacity: 0,
          y: 40,
          rotateX: 15
        }} animate={{
          opacity: 1,
          y: 0,
          rotateX: 0
        }} transition={{
          duration: 1,
          delay: 0.4
        }} className="relative max-w-6xl mx-auto perspective-2000" data-id="element-2469">
            <div className="relative bg-[#1A1A1C] rounded-[32px] border border-white/10 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] overflow-hidden p-4 md:p-8" data-id="element-2470">
              {/* Mockup Header */}
              <div className="flex items-center justify-between mb-8 border-b border-white/5 pb-6" data-id="element-2471">
                <div className="flex items-center gap-4" data-id="element-2472">
                  <div className="flex gap-1.5" data-id="element-2473">
                    <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/40" data-id="element-2474"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/40" data-id="element-2475"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/40" data-id="element-2476"></div>
                  </div>
                  <div className="h-4 w-32 bg-white/5 rounded-full" data-id="element-2477"></div>
                </div>
                <div className="flex items-center gap-4" data-id="element-2478">
                  <div className="h-8 w-8 rounded-lg bg-white/5 border border-white/10" data-id="element-2479"></div>
                  <div className="h-8 w-8 rounded-lg bg-white/5 border border-white/10" data-id="element-2480"></div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-2481">
                {[{
                label: 'Total Revenue',
                value: '$124,500',
                trend: '+12.4%',
                color: 'text-[#10B981]'
              }, {
                label: 'Food Cost %',
                value: '28.4%',
                trend: '-2.1%',
                color: 'text-[#10B981]'
              }, {
                label: 'Labor Cost %',
                value: '31.2%',
                trend: '+0.4%',
                color: 'text-red-400'
              }].map((stat, i) => <div key={i} className="p-6 bg-white/5 border border-white/10 rounded-2xl" data-id="element-2482">
                    <p className="text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-2483">
                      {stat.label}
                    </p>
                    <div className="flex items-end justify-between" data-id="element-2484">
                      <p className="text-3xl font-black" data-id="element-2485">{stat.value}</p>
                      <span className={`text-xs font-bold ${stat.color}`} data-id="element-2486">
                        {stat.trend}
                      </span>
                    </div>
                  </div>)}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2487">
                <div className="h-64 bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col" data-id="element-2488">
                  <div className="flex items-center justify-between mb-6" data-id="element-2489">
                    <h4 className="text-sm font-bold text-white/60" data-id="element-2490">
                      Weekly Profit Margin
                    </h4>
                    <BarChart3 size={18} className="text-white/20" data-id="element-2491" />
                  </div>
                  <div className="flex-1 flex items-end gap-2" data-id="element-2492">
                    {[40, 70, 45, 90, 65, 80, 55, 95, 75, 85].map((h, i) => <motion.div key={i} initial={{
                    height: 0
                  }} animate={{
                    height: `${h}%`
                  }} transition={{
                    delay: 1 + i * 0.05
                  }} className={`flex-1 rounded-t-lg ${i === 7 ? 'bg-[#10B981]' : 'bg-white/10'}`} data-id="element-2493" />)}
                  </div>
                </div>
                <div className="h-64 bg-white/5 border border-white/10 rounded-2xl p-6" data-id="element-2494">
                  <div className="flex items-center justify-between mb-6" data-id="element-2495">
                    <h4 className="text-sm font-bold text-white/60" data-id="element-2496">
                      Expense Breakdown
                    </h4>
                    <PieChart size={18} className="text-white/20" data-id="element-2497" />
                  </div>
                  <div className="space-y-4" data-id="element-2498">
                    {[{
                    label: 'Food & Beverage',
                    value: '35%',
                    width: 'w-[35%]',
                    color: 'bg-[#10B981]'
                  }, {
                    label: 'Labor & Payroll',
                    value: '32%',
                    width: 'w-[32%]',
                    color: 'bg-emerald-600'
                  }, {
                    label: 'Rent & Utilities',
                    value: '15%',
                    width: 'w-[15%]',
                    color: 'bg-emerald-800'
                  }, {
                    label: 'Other',
                    value: '18%',
                    width: 'w-[18%]',
                    color: 'bg-white/10'
                  }].map((item, i) => <div key={i} data-id="element-2499">
                        <div className="flex justify-between text-xs font-medium mb-1.5" data-id="element-2500">
                          <span data-id="element-2501">{item.label}</span>
                          <span className="text-white/40" data-id="element-2502">{item.value}</span>
                        </div>
                        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden" data-id="element-2503">
                          <motion.div initial={{
                        width: 0
                      }} animate={{
                        width: item.value
                      }} transition={{
                        duration: 1,
                        delay: 1.5
                      }} className={`h-full ${item.color}`} data-id="element-2504" />
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <motion.div animate={{
            y: [0, -20, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-12 -right-12 hidden lg:block p-6 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl z-20" data-id="element-2505">
              <div className="flex items-center gap-4 mb-4" data-id="element-2506">
                <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center text-red-400" data-id="element-2507">
                  <AlertCircle size={20} data-id="element-2508" />
                </div>
                <div data-id="element-2509">
                  <p className="text-xs font-bold text-white/40 uppercase" data-id="element-2510">
                    Inventory Alert
                  </p>
                  <p className="text-sm font-bold" data-id="element-2511">Wagyu Beef Low</p>
                </div>
              </div>
              <div className="h-1.5 w-32 bg-white/5 rounded-full overflow-hidden" data-id="element-2512">
                <div className="h-full w-1/4 bg-red-500" data-id="element-2513"></div>
              </div>
            </motion.div>

            <motion.div animate={{
            y: [0, 20, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -bottom-12 -left-12 hidden lg:block p-6 bg-[#10B981]/10 backdrop-blur-2xl border border-[#10B981]/20 rounded-2xl shadow-2xl z-20" data-id="element-2514">
              <div className="flex items-center gap-4" data-id="element-2515">
                <div className="w-10 h-10 bg-[#10B981]/20 rounded-full flex items-center justify-center text-[#10B981]" data-id="element-2516">
                  <TrendingDown size={20} data-id="element-2517" />
                </div>
                <div data-id="element-2518">
                  <p className="text-xs font-bold text-white/40 uppercase" data-id="element-2519">
                    Cost Reduction
                  </p>
                  <p className="text-sm font-bold" data-id="element-2520">Waste down 14%</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Trust Row */}
          <div className="mt-32 pt-20 border-t border-white/5" data-id="element-2521">
            <p className="text-center text-white/40 font-bold uppercase tracking-[0.3em] text-xs mb-12" data-id="element-2522">
              Built for restaurant operators who are tired of:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 text-center" data-id="element-2523">
              {['Unknown Margins', 'Overspending', 'Financial Lag', 'Manual Inventory', 'Zero Visibility'].map((item, i) => <div key={i} className="text-white/60 font-medium hover:text-white transition-colors cursor-default" data-id="element-2524">
                  {item}
                </div>)}
            </div>
            <p className="text-center mt-16 text-xl font-medium text-white/80 italic" data-id="element-2525">
              "ROI gives operators a faster way to understand the health of
              their business."
            </p>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problem" className="py-32 px-6 bg-[#0D0D0E] relative overflow-hidden" data-id="element-2526">
        <div className="max-w-7xl mx-auto" data-id="element-2527">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-2528">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-2529">
              <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-tight mb-8" data-id="element-2530">
                Most Restaurant Owners Know Sales… <br data-id="element-2531" />
                <span className="text-red-500" data-id="element-2532">
                  But Don’t Know What They Actually Keep.
                </span>
              </h2>
              <p className="text-xl text-white/60 font-medium leading-relaxed mb-12" data-id="element-2533">
                You might make $20,000 this week… But: How much went to food
                costs? Labor? Utilities? Waste? Inventory loss? Overspending?
              </p>
              <div className="space-y-6" data-id="element-2534">
                {[{
                label: 'Food Cost Leaks',
                desc: 'Untracked price fluctuations from suppliers.'
              }, {
                label: 'Labor Inefficiency',
                desc: 'Scheduling without real-time demand data.'
              }, {
                label: 'Inventory Shrinkage',
                desc: 'Waste and theft that goes unnoticed for weeks.'
              }].map((item, i) => <div key={i} className="flex gap-6 p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-red-500/30 transition-all group" data-id="element-2535">
                    <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center text-red-500 shrink-0 group-hover:scale-110 transition-transform" data-id="element-2536">
                      <TrendingDown size={24} data-id="element-2537" />
                    </div>
                    <div data-id="element-2538">
                      <h4 className="text-lg font-bold mb-1" data-id="element-2539">{item.label}</h4>
                      <p className="text-white/40 text-sm" data-id="element-2540">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
              <p className="mt-12 text-lg font-bold text-white/80" data-id="element-2541">
                Most operators never see the full picture until it’s already
                hurting the business.{' '}
                <span className="text-[#10B981]" data-id="element-2542">ROI changes that.</span>
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-2543">
              <div className="aspect-square bg-gradient-to-br from-red-500/20 to-transparent rounded-full blur-[100px] absolute inset-0" data-id="element-2544"></div>
              <div className="relative bg-[#1A1A1C] rounded-[40px] border border-white/10 p-12 shadow-2xl overflow-hidden" data-id="element-2545">
                <div className="text-center mb-12" data-id="element-2546">
                  <h3 className="text-2xl font-black mb-2" data-id="element-2547">
                    The Financial Leak
                  </h3>
                  <p className="text-white/40 text-sm font-medium" data-id="element-2548">
                    Where your profit disappears
                  </p>
                </div>

                <div className="relative h-64 flex items-center justify-center" data-id="element-2549">
                  {/* Animated Leak Visual */}
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-2550">
                    <motion.div animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3]
                  }} transition={{
                    duration: 3,
                    repeat: Infinity
                  }} className="w-48 h-48 bg-red-500/20 rounded-full blur-3xl" data-id="element-2551"></motion.div>
                  </div>
                  <div className="relative z-10 text-center" data-id="element-2552">
                    <motion.p animate={{
                    y: [0, 10, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity
                  }} className="text-6xl font-black text-red-500 mb-2" data-id="element-2553">
                      -$4,280
                    </motion.p>
                    <p className="text-sm font-bold text-white/40 uppercase tracking-widest" data-id="element-2554">
                      Weekly Leakage
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-8" data-id="element-2555">
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10" data-id="element-2556">
                    <p className="text-[10px] font-bold text-white/40 uppercase mb-1" data-id="element-2557">
                      Waste
                    </p>
                    <p className="text-lg font-bold text-red-400" data-id="element-2558">-$1,240</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10" data-id="element-2559">
                    <p className="text-[10px] font-bold text-white/40 uppercase mb-1" data-id="element-2560">
                      Over-staffing
                    </p>
                    <p className="text-lg font-bold text-red-400" data-id="element-2561">-$2,040</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-32 px-6 relative" data-id="element-2562">
        <div className="max-w-7xl mx-auto" data-id="element-2563">
          <div className="text-center max-w-3xl mx-auto mb-24" data-id="element-2564">
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-8" data-id="element-2565">
              Operational Clarity — <br data-id="element-2566" />
              <span className="text-[#10B981]" data-id="element-2567">Without the Complexity</span>
            </h2>
            <p className="text-xl text-white/60 font-medium" data-id="element-2568">
              All designed around how restaurants actually operate. Not generic
              accounting software.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20" data-id="element-2569">
            {[{
            title: 'Track Money In/Out',
            icon: Wallet,
            desc: 'Real-time cash flow monitoring across all locations.'
          }, {
            title: 'Monitor Food & Labor',
            icon: ChefHat,
            desc: 'Dynamic cost analysis that updates as prices change.'
          }, {
            title: 'Inventory Visibility',
            icon: Layers,
            desc: 'Predictive stock management to eliminate waste.'
          }, {
            title: 'Faster Decisions',
            icon: Zap,
            desc: 'Actionable insights delivered when they matter most.'
          }, {
            title: 'Identify Profit Leaks',
            icon: AlertCircle,
            desc: 'AI-driven detection of operational inefficiencies.'
          }, {
            title: 'Operational Spending',
            icon: DollarSign,
            desc: 'Complete transparency into every dollar spent.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} whileHover={{
            y: -10
          }} className="p-8 bg-white/5 border border-white/10 rounded-[32px] hover:border-[#10B981]/30 transition-all group" data-id="element-2570">
                <div className="w-14 h-14 bg-[#10B981]/10 rounded-2xl flex items-center justify-center text-[#10B981] mb-8 group-hover:bg-[#10B981] group-hover:text-white transition-all duration-500" data-id="element-2571">
                  <item.icon size={28} data-id="element-2572" />
                </div>
                <h3 className="text-2xl font-black mb-4" data-id="element-2573">{item.title}</h3>
                <p className="text-white/40 font-medium leading-relaxed" data-id="element-2574">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>

          <div className="relative bg-gradient-to-br from-[#10B981]/20 to-transparent rounded-[40px] p-12 border border-[#10B981]/20 overflow-hidden" data-id="element-2575">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10" data-id="element-2576">
              <div data-id="element-2577">
                <h3 className="text-3xl font-black mb-6" data-id="element-2578">
                  Seamless Integration
                </h3>
                <p className="text-white/60 text-lg mb-8 leading-relaxed" data-id="element-2579">
                  ROI connects directly to your POS, supplier portals, and
                  payroll systems to create a unified intelligence layer.
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-2580">
                  {['Toast', 'Square', 'Clover', 'Lightspeed', 'ADP', 'Sysco'].map(brand => <div key={brand} className="px-6 py-3 bg-white/5 border border-white/10 rounded-xl text-sm font-bold text-white/40" data-id="element-2581">
                      {brand}
                    </div>)}
                </div>
              </div>
              <div className="relative h-64 flex items-center justify-center" data-id="element-2582">
                {/* Animated Connecting Lines Visual */}
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-2583">
                  <div className="w-24 h-24 bg-[#10B981] rounded-2xl flex items-center justify-center shadow-[0_0_50px_rgba(16,185,129,0.5)] z-10" data-id="element-2584">
                    <TrendingUp size={40} data-id="element-2585" />
                  </div>
                  {[0, 60, 120, 180, 240, 300].map(deg => <motion.div key={deg} animate={{
                  opacity: [0.2, 1, 0.2]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: deg / 360
                }} className="absolute w-40 h-[1px] bg-gradient-to-r from-transparent via-[#10B981] to-transparent" style={{
                  transform: `rotate(${deg}deg)`
                }} data-id="element-2586"></motion.div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiator Section */}
      <section className="py-32 px-6 bg-[#0D0D0E]" data-id="element-2587">
        <div className="max-w-7xl mx-auto" data-id="element-2588">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-2589">
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="lg:order-2" data-id="element-2590">
              <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-8" data-id="element-2591">
                Built for <span className="text-[#10B981]" data-id="element-2592">Speed</span>
              </h2>
              <p className="text-xl text-white/60 font-medium leading-relaxed mb-12" data-id="element-2593">
                Most systems take months to implement. ROI is designed to help
                operators start seeing insights quickly — so you can make
                informed decisions sooner, not later.
              </p>

              <div className="space-y-8" data-id="element-2594">
                <div className="flex items-center gap-6" data-id="element-2595">
                  <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center text-white/40 font-black" data-id="element-2596">
                    01
                  </div>
                  <div data-id="element-2597">
                    <h4 className="text-lg font-bold" data-id="element-2598">Instant Data Sync</h4>
                    <p className="text-white/40 text-sm" data-id="element-2599">
                      Connect your POS in under 5 minutes.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6" data-id="element-2600">
                  <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center text-white/40 font-black" data-id="element-2601">
                    02
                  </div>
                  <div data-id="element-2602">
                    <h4 className="text-lg font-bold" data-id="element-2603">Automated Mapping</h4>
                    <p className="text-white/40 text-sm" data-id="element-2604">
                      Our AI categorizes your expenses automatically.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6" data-id="element-2605">
                  <div className="w-12 h-12 bg-[#10B981]/20 rounded-full flex items-center justify-center text-[#10B981] font-black" data-id="element-2606">
                    03
                  </div>
                  <div data-id="element-2607">
                    <h4 className="text-lg font-bold" data-id="element-2608">Live in 48 Hours</h4>
                    <p className="text-white/40 text-sm" data-id="element-2609">
                      Start seeing real-time profit visibility by day two.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="lg:order-1" data-id="element-2610">
              <div className="grid grid-cols-2 gap-4" data-id="element-2611">
                {[{
                label: 'One Restaurant',
                icon: Utensils
              }, {
                label: 'Café',
                icon: ChefHat
              }, {
                label: 'Deli',
                icon: Layers
              }, {
                label: 'Multiple Locations',
                icon: Globe
              }].map((item, i) => <motion.div key={i} whileHover={{
                scale: 1.05
              }} className="p-8 bg-white/5 border border-white/10 rounded-3xl text-center flex flex-col items-center gap-4" data-id="element-2612">
                    <item.icon size={32} className="text-[#10B981]" data-id="element-2613" />
                    <p className="font-bold text-sm" data-id="element-2614">{item.label}</p>
                  </motion.div>)}
              </div>
              <p className="text-center mt-8 text-white/40 font-bold uppercase tracking-widest text-xs" data-id="element-2615">
                ROI adapts to your workflow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section id="benefits" className="py-32 px-6" data-id="element-2616">
        <div className="max-w-7xl mx-auto" data-id="element-2617">
          <div className="text-center max-w-3xl mx-auto mb-24" data-id="element-2618">
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-8" data-id="element-2619">
              What Restaurant <br data-id="element-2620" />
              <span className="text-[#10B981]" data-id="element-2621">Owners Gain</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2622">
            {[{
            title: 'Better Visibility',
            desc: 'Understand where your money is actually going.',
            metric: '100%',
            metricLabel: 'Transparency',
            icon: Eye
          }, {
            title: 'Faster Decisions',
            desc: 'Know what needs attention before it becomes a bigger problem.',
            metric: '4x',
            metricLabel: 'Faster Action',
            icon: Zap
          }, {
            title: 'Operational Control',
            desc: 'Track spending habits, inventory behavior, and business performance.',
            metric: '22%',
            metricLabel: 'Cost Savings',
            icon: LayoutDashboard
          }, {
            title: 'Scalability',
            desc: 'Create systems that grow with your restaurant.',
            metric: '∞',
            metricLabel: 'Growth Potential',
            icon: TrendingUp
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group relative bg-[#1A1A1C] rounded-[40px] border border-white/10 p-10 overflow-hidden" data-id="element-2623">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#10B981]/5 blur-[80px] rounded-full -mr-32 -mt-32 group-hover:bg-[#10B981]/10 transition-colors duration-700" data-id="element-2624"></div>

                <div className="relative z-10 flex flex-col h-full" data-id="element-2625">
                  <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center text-[#10B981] mb-8" data-id="element-2626">
                    <item.icon size={32} data-id="element-2627" />
                  </div>
                  <h3 className="text-3xl font-black mb-4" data-id="element-2628">{item.title}</h3>
                  <p className="text-white/60 text-lg mb-12 leading-relaxed" data-id="element-2629">
                    {item.desc}
                  </p>

                  <div className="mt-auto pt-8 border-t border-white/5 flex items-end justify-between" data-id="element-2630">
                    <div data-id="element-2631">
                      <p className="text-5xl font-black text-white mb-1" data-id="element-2632">
                        {item.metric}
                      </p>
                      <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-2633">
                        {item.metricLabel}
                      </p>
                    </div>
                    <div className="w-24 h-12 bg-white/5 rounded-xl border border-white/10 flex items-center justify-center" data-id="element-2634">
                      <LineChart size={24} className="text-[#10B981]" data-id="element-2635" />
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Soft Authority Section */}
      <section id="about" className="py-32 px-6 bg-[#0D0D0E]" data-id="element-2636">
        <div className="max-w-7xl mx-auto" data-id="element-2637">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-2638">
            <div className="relative rounded-[40px] overflow-hidden aspect-[4/5] group" data-id="element-2639">
              <img src="https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&q=80&w=1200" alt="Restaurant Operations" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[2s]" data-id="element-2640" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" data-id="element-2641"></div>
              <div className="absolute bottom-12 left-12 right-12" data-id="element-2642">
                <div className="p-8 bg-black/40 backdrop-blur-2xl border border-white/10 rounded-3xl" data-id="element-2643">
                  <p className="text-lg font-medium italic text-white/90 mb-4" data-id="element-2644">
                    "We built ROI because we were tired of generic tools that
                    didn't understand how a kitchen actually runs."
                  </p>
                  <div className="flex items-center gap-4" data-id="element-2645">
                    <div className="w-12 h-12 rounded-full bg-[#10B981]" data-id="element-2646"></div>
                    <div data-id="element-2647">
                      <p className="font-bold" data-id="element-2648">Marcus Chen</p>
                      <p className="text-xs text-white/40 font-bold uppercase" data-id="element-2649">
                        Founder & Former Operator
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div data-id="element-2650">
              <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-8" data-id="element-2651">
                Built Around Real <br data-id="element-2652" />
                <span className="text-[#10B981]" data-id="element-2653">Restaurant Operations</span>
              </h2>
              <div className="space-y-8 text-xl text-white/60 font-medium leading-relaxed" data-id="element-2654">
                <p data-id="element-2655">
                  ROI was created from years of hands-on restaurant operational
                  experience — built specifically around the day-to-day
                  realities restaurant owners face.
                </p>
                <p data-id="element-2656">
                  This isn’t generic software built by people outside the
                  industry. It’s designed for operators.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-8 mt-12" data-id="element-2657">
                <div className="space-y-2" data-id="element-2658">
                  <h4 className="text-3xl font-black text-white" data-id="element-2659">15+</h4>
                  <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-2660">
                    Years in Operations
                  </p>
                </div>
                <div className="space-y-2" data-id="element-2661">
                  <h4 className="text-3xl font-black text-white" data-id="element-2662">500+</h4>
                  <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-2663">
                    Kitchens Optimized
                  </p>
                </div>
              </div>

              <button className="mt-12 px-10 py-5 border border-white/10 rounded-full font-black text-lg hover:bg-white hover:text-black transition-all duration-500" data-id="element-2664">
                Read Our Story
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-40 px-6 relative overflow-hidden" data-id="element-2665">
        <div className="absolute inset-0 bg-[#10B981]/5" data-id="element-2666"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none" data-id="element-2667">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-[#10B981]/10 blur-[150px] rounded-full" data-id="element-2668"></div>
        </div>

        <div className="max-w-5xl mx-auto relative z-10 text-center" data-id="element-2669">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2670">
            <h2 className="text-5xl md:text-8xl font-black tracking-tighter mb-8 leading-[0.9]" data-id="element-2671">
              Ready to See What Your <br data-id="element-2672" />
              <span className="text-[#10B981]" data-id="element-2673">Numbers Are Really Saying?</span>
            </h2>
            <p className="text-xl md:text-2xl text-white/60 font-medium mb-12 max-w-2xl mx-auto leading-relaxed" data-id="element-2674">
              Book a quick walkthrough and see how ROI can help you better
              understand your restaurant operations.
            </p>
            <button className="px-12 py-6 bg-[#10B981] text-white rounded-full font-black text-xl hover:bg-[#059669] transition-all duration-500 shadow-[0_20px_50px_rgba(16,185,129,0.3)] group flex items-center justify-center gap-3 mx-auto" data-id="element-2675">
              Book Your Demo
              <ArrowRight size={24} className="group-hover:translate-x-2 transition-transform" data-id="element-2676" />
            </button>
            <p className="mt-8 text-white/40 font-bold uppercase tracking-widest text-xs" data-id="element-2677">
              No credit card required • 15-minute walkthrough
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 px-6 border-t border-white/5 bg-[#0A0A0B]" data-id="element-2678">
        <div className="max-w-7xl mx-auto" data-id="element-2679">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-20" data-id="element-2680">
            <div className="col-span-2 md:col-span-1" data-id="element-2681">
              <div className="flex items-center gap-2 mb-8" data-id="element-2682">
                <div className="w-8 h-8 bg-[#10B981] rounded-lg flex items-center justify-center" data-id="element-2683">
                  <TrendingUp size={20} data-id="element-2684" />
                </div>
                <span className="text-xl font-black tracking-tighter" data-id="element-2685">ROI</span>
              </div>
              <p className="text-white/40 text-sm leading-relaxed max-w-xs" data-id="element-2686">
                The premium intelligence platform for serious restaurant
                operators. Built for speed, clarity, and control.
              </p>
            </div>
            <div data-id="element-2687">
              <h4 className="text-xs font-black uppercase tracking-widest text-white mb-6" data-id="element-2688">
                Product
              </h4>
              <ul className="space-y-4 text-sm font-medium text-white/40" data-id="element-2689">
                <li data-id="element-2690">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2691">
                    Features
                  </a>
                </li>
                <li data-id="element-2692">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2693">
                    Integrations
                  </a>
                </li>
                <li data-id="element-2694">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2695">
                    Pricing
                  </a>
                </li>
                <li data-id="element-2696">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2697">
                    Changelog
                  </a>
                </li>
              </ul>
            </div>
            <div data-id="element-2698">
              <h4 className="text-xs font-black uppercase tracking-widest text-white mb-6" data-id="element-2699">
                Company
              </h4>
              <ul className="space-y-4 text-sm font-medium text-white/40" data-id="element-2700">
                <li data-id="element-2701">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2702">
                    About Us
                  </a>
                </li>
                <li data-id="element-2703">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2704">
                    Careers
                  </a>
                </li>
                <li data-id="element-2705">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2706">
                    Privacy
                  </a>
                </li>
                <li data-id="element-2707">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2708">
                    Terms
                  </a>
                </li>
              </ul>
            </div>
            <div data-id="element-2709">
              <h4 className="text-xs font-black uppercase tracking-widest text-white mb-6" data-id="element-2710">
                Connect
              </h4>
              <ul className="space-y-4 text-sm font-medium text-white/40" data-id="element-2711">
                <li data-id="element-2712">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2713">
                    Twitter
                  </a>
                </li>
                <li data-id="element-2714">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2715">
                    LinkedIn
                  </a>
                </li>
                <li data-id="element-2716">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2717">
                    Instagram
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-2718">
            <p className="text-xs font-bold text-white/20 uppercase tracking-widest" data-id="element-2719">
              © 2026 ROI Intelligence Inc. All rights reserved.
            </p>
            <div className="flex items-center gap-2 text-white/20 text-xs font-bold uppercase tracking-widest" data-id="element-2720">
              <span data-id="element-2721">Built for Operators</span>
              <span className="w-1 h-1 bg-white/10 rounded-full" data-id="element-2722"></span>
              <span data-id="element-2723">Enterprise Ready</span>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}